import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Flame, ArrowLeft, ArrowRight, Truck, AlertCircle, Package, UserCircle2, Snowflake, Store, Facebook, Instagram } from "lucide-react";
import { CartBadge, AddToCartButton, ShopCmsBanner } from "./ShopCart";
import { useShopAuth } from "../lib/shopAuth";
import { useI18n } from "../lib/i18n";
import ProtectedImg from "../components/ProtectedImg";
import { CatalogQtyControl } from "../components/shop/CatalogQtyControl";
import { MobileCartBar } from "../components/shop/MobileCartBar";
import { LastOrderBanner } from "../components/shop/LastOrderBanner";

const API = process.env.REACT_APP_BACKEND_URL;
const CATALOG_SCROLL_KEY = "shop_catalog_scroll";
const MOBILE_QUERY = "(max-width: 767px)";
let catalogCache = null;

/**
 * Retourne le champ d'un produit dans la langue active, avec fallback FR.
 * Ex: localized(p, "name", "de") → p.name_de || p.name
 * Phase 53 : si le champ traduit existe en DE/IT, on l'utilise. Sinon fallback FR.
 */
function localized(product, field, lang) {
  if (!product) return "";
  if (lang && lang !== "FR") {
    const localizedKey = `${field}_${lang.toLowerCase()}`;
    if (product[localizedKey] && String(product[localizedKey]).trim()) {
      return product[localizedKey];
    }
  }
  return product[field] || "";
}

// Résout une URL photo : si elle commence par "/" → préfixe avec le backend public.
// `width` optionnel : ajoute `?w=N` pour bénéficier du resize serveur (cache LRU).
// Tailles autorisées côté backend : 200, 400, 600, 800, 1200.
//
// Optimisation perf : on transforme `/photo` → `/photo.webp` (et `/photo-packaging`
// → `/photo-packaging.webp`) pour que Cloudflare cache automatiquement l'image
// par extension de fichier (cf-cache-status: HIT au lieu de DYNAMIC).
// Le `?v=timestamp` dans `photo_url` invalide proprement le cache à chaque upload.
function resolvePhotoUrl(url, width) {
  if (!url) return "";
  let path = url;
  // Ajoute .webp aux endpoints photo/photo-packaging si pas déjà présent.
  if (path.includes("/api/shop/products/") && /\/photo(-packaging)?(\?|$)/.test(path)) {
    path = path.replace(/\/photo(-packaging)?(\?|$)/, "/photo$1.webp$2");
  }
  let full = path.startsWith("/") ? `${API}${path}` : path;
  if (width && full.includes(`${API}/api/shop/products/`)) {
    const sep = full.includes("?") ? "&" : "?";
    full += `${sep}w=${width}`;
  }
  return full;
}

// ============== Shared shop styles & helpers ==============
const COLORS = {
  bg: "#0A0A0A",
  surface: "#141414",
  surfaceHover: "#1F1F1F",
  primary: "#F2ECE4",
  secondary: "#A6A29C",
  muted: "#737373",
  chili: "#E54D2E",
  chiliHover: "#F06B52",
  gold: "#C1A063",
  basil: "#88A066",
};

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/976f654f-46db-4893-960d-13da8eb93d7a/images/093b47915d96c7e8e2ca4d7b4f1ff30585e9019b74108c2225716d9055d16451.png";

function SpiceIcons({ level = 0, size = 14 }) {
  if (level == null) return null;
  return (
    <div className="flex gap-0.5" data-testid={`spice-${level}`}>
      {[0, 1, 2].map((i) => (
        <Flame
          key={i}
          size={size}
          strokeWidth={2}
          style={{ color: i < level ? COLORS.chili : "#333333" }}
          className="transition-colors"
          fill={i < level ? COLORS.chili : "none"}
        />
      ))}
    </div>
  );
}

function ShopHeader() {
  const { customer } = useShopAuth() || {};
  const { lang, setLang, t } = useI18n();
  const isAuthed = customer && customer !== false;
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b"
      style={{ background: "rgba(10,10,10,0.78)", borderColor: "rgba(242,236,228,0.08)" }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-12 flex justify-between items-center py-3 sm:py-5 gap-2 sm:gap-4">
        <Link to="/boutique" className="block shrink-0" data-testid="shop-logo">
          <span
            className="text-2xl leading-none whitespace-nowrap block"
            style={{ fontFamily: "'Sayonara', cursive", color: COLORS.primary, fontWeight: 700 }}
          >
            Maison Thaï <span className="hidden 2xl:inline" style={{ color: COLORS.gold }}>Conthey</span>
          </span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-6 font-body text-[10px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] shrink-0">
          <div className="flex items-center gap-1.5 sm:gap-6">
            {["FR", "DE", "IT"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className="transition-colors text-[10px] sm:text-xs"
                style={{
                  color: lang === l ? COLORS.primary : COLORS.muted,
                  fontWeight: lang === l ? 600 : 400,
                }}
                data-testid={`lang-selector-${l.toLowerCase()}`}
                aria-label={`Switch language to ${l}`}
              >
                {l}
              </button>
            ))}
            <div className="hidden sm:block w-px h-4" style={{ background: "rgba(242,236,228,0.15)" }} />
          </div>
          {/* Phase 98n — Lien direct "Devenir revendeur" pour les visiteurs pro (visible ≥ xl uniquement) */}
          <Link
            to="/devenir-revendeur"
            className="hidden xl:inline transition-colors hover:opacity-90 text-[10px] sm:text-xs"
            style={{ color: COLORS.gold }}
            data-testid="header-become-reseller"
          >
            {t("header.reseller")}
          </Link>
          {/* Phase 100 — Lien discret « Devenir hôte » retiré du header, dispo dans le footer */}
          <div className="hidden xl:block w-px h-4" style={{ background: "rgba(242,236,228,0.15)" }} />
          <CartBadge />
          <div className="w-px h-4" style={{ background: "rgba(242,236,228,0.15)" }} />
          {isAuthed ? (
            <Link
              to="/boutique/compte"
              className="flex items-center gap-1.5 transition-colors hover:opacity-90"
              style={{ color: COLORS.gold }}
              data-testid="shop-account-link"
              title={customer.email}
            >
              <UserCircle2 className="w-4 h-4" />
              <span className="hidden sm:inline">{(customer.name || t("header.account")).split(" ")[0]}</span>
            </Link>
          ) : (
            <Link
              to="/boutique/login"
              className="transition-colors hover:opacity-90"
              style={{ color: COLORS.secondary }}
              data-testid="shop-login-link"
            >
              {t("header.login")}
            </Link>
          )}
          <div className="w-px h-4" style={{ background: "rgba(242,236,228,0.15)" }} />
          <Link
            to="/login"
            className="opacity-30 hover:opacity-90 transition-opacity text-[10px]"
            style={{ color: COLORS.secondary }}
            data-testid="admin-link"
          >
            {t("header.admin")}
          </Link>
        </nav>
      </div>
    </header>
  );
}

function DeliveryBanner() {
  const { t } = useI18n();
  return (
    <div
      className="text-center py-2 sm:py-3 px-3 sm:px-6 text-[10px] sm:text-xs tracking-[0.18em] sm:tracking-[0.25em] uppercase border-b font-body"
      style={{ background: COLORS.surface, color: COLORS.gold, borderColor: "rgba(193,160,99,0.18)" }}
      data-testid="delivery-banner"
    >
      <Snowflake className="inline-block w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1.5 sm:mr-2 -mt-0.5" />
      <span className="hidden sm:inline">{t("banner.isotherm_short")} · </span>
      <span className="sm:hidden">{t("banner.isotherm_mobile")} · </span>
      <Truck className="inline-block w-3 h-3 sm:w-3.5 sm:h-3.5 mx-1.5 sm:mx-2 -mt-0.5" />
      <span>{t("banner.free_valais")}</span>
    </div>
  );
}

// Mini-bloc explicatif sous le hero : envoi national isotherme.
function IsothermShippingNote() {
  const { t } = useI18n();
  return (
    <section
      className="border-y"
      style={{
        background:
          "linear-gradient(90deg, rgba(193,160,99,0.06) 0%, rgba(136,160,102,0.04) 100%)",
        borderColor: "rgba(193,160,99,0.18)",
      }}
      data-testid="isotherm-shipping-note"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-12 py-5 sm:py-7 flex items-start sm:items-center gap-4 sm:gap-6 flex-wrap sm:flex-nowrap">
        <div
          className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center"
          style={{ background: "rgba(193,160,99,0.12)", border: "1px solid rgba(193,160,99,0.35)" }}
        >
          <Snowflake className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: COLORS.gold }} />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[10px] sm:text-[11px] tracking-[0.25em] uppercase mb-1"
            style={{ color: COLORS.gold }}
          >
            {t("isotherm_note.kicker")}
          </p>
          <h3
            className="font-heading text-lg sm:text-xl md:text-2xl leading-tight mb-1.5"
            style={{ color: COLORS.primary }}
          >
            {t("isotherm_note.title")}
          </h3>
          <p
            className="text-xs sm:text-sm leading-relaxed"
            style={{ color: COLORS.secondary }}
          >
            {t("isotherm_note.body_prefix")}{" "}
            <strong style={{ color: COLORS.primary }}>{t("isotherm_note.body_temp")}</strong>{" "}
            {t("isotherm_note.body_middle")}{" "}
            <strong style={{ color: COLORS.primary }}>{t("isotherm_note.body_forfait")}</strong>{" "}
            {t("isotherm_note.body_for")}{" "}
            <a
              href="mailto:contact@maison-thai.ch?subject=Envoi%20postal%20personnalis%C3%A9"
              className="underline hover:no-underline transition-colors"
              style={{ color: COLORS.chili }}
              data-testid="isotherm-contact-email"
            >
              {t("isotherm_note.body_contact")}
            </a>
            {t("isotherm_note.body_suffix")}
          </p>
        </div>
      </div>
    </section>
  );
}

// Phase 100 (12/07/2026) — Bannière de recrutement d'hôtes distributeurs.
// FR UNIQUEMENT (choix du dirigeant : Valais francophone cible unique). Les
// visiteurs DE/IT ne voient rien — ils viendront depuis la version FR s'ils
// s'intéressent au sujet. Placée entre le savoir-faire et le bandeau revendeur.
function HostRecruitmentBanner() {
  const { lang } = useI18n();
  if (lang !== "FR") return null;
  return (
    <section
      className="border-y"
      style={{
        background:
          "linear-gradient(90deg, rgba(31,78,61,0.85) 0%, rgba(23,61,47,0.92) 100%)",
        borderColor: "rgba(193,160,99,0.35)",
      }}
      data-testid="host-recruitment-banner"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-12 py-8 sm:py-10 flex items-center gap-5 sm:gap-8 flex-wrap sm:flex-nowrap">
        <div
          className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center"
          style={{ background: "rgba(193,160,99,0.18)", border: "1px solid rgba(193,160,99,0.55)" }}
        >
          <Store className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: COLORS.gold }} />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[10px] sm:text-[11px] tracking-[0.25em] uppercase mb-1"
            style={{ color: COLORS.gold }}
          >
            Partenariat commercial · Valais
          </p>
          <h3
            className="font-heading text-lg sm:text-xl md:text-2xl leading-tight mb-2"
            style={{ color: COLORS.primary }}
          >
            Vous avez un commerce en Valais ?
          </h3>
          <p
            className="text-xs sm:text-sm leading-relaxed mb-4 sm:mb-0"
            style={{ color: COLORS.secondary }}
          >
            Accueillez un <strong style={{ color: COLORS.primary }}>distributeur automatique Maison Thaï</strong>.
            Zéro gestion pour vous — nous nous occupons du réappro, de la traçabilité et des DLC.
            Vous, vous encaissez une participation aux ventes.
          </p>
        </div>
        <div className="flex-shrink-0 w-full sm:w-auto">
          <Link
            to="/hote-distributeur"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-xs sm:text-sm uppercase tracking-[0.18em] font-semibold transition-all hover:scale-[1.02]"
            style={{ background: COLORS.gold, color: "#1F4E3D" }}
            data-testid="host-banner-cta"
          >
            Découvrir le programme
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// Bandeau marque permanent (identique à celui apposé sur les voitures Maison Thaï).
// Affiché sous le header / au-dessus du contenu, sur toutes les pages /boutique.
function BrandStripeBar({ topbar }) {
  if (!topbar || !topbar.slug) return null;
  const imgUrl = `${API}/api/shop/boutique-section/${topbar.slug}/photo${topbar.uploaded_at ? `?v=${encodeURIComponent(topbar.uploaded_at)}` : ""}`;
  return (
    <div
      className="relative w-full overflow-hidden border-b"
      style={{ background: "#0A0A0A", borderColor: "rgba(193,160,99,0.18)" }}
      data-testid="brand-stripe-bar"
    >
      <div
        className="w-full"
        style={{
          height: "clamp(56px, 9vw, 96px)",
          backgroundImage: `url(${imgUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center 38%",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div
        className="absolute inset-0 flex items-center justify-center px-4"
        style={{ background: "linear-gradient(90deg, rgba(10,10,10,0.65) 0%, rgba(10,10,10,0.15) 50%, rgba(10,10,10,0.65) 100%)" }}
      >
        <p
          className="font-body uppercase tracking-[0.25em] sm:tracking-[0.4em] text-[10px] sm:text-xs md:text-sm text-center whitespace-nowrap overflow-hidden text-ellipsis"
          style={{ color: COLORS.primary, textShadow: "0 1px 8px rgba(0,0,0,0.85)" }}
        >
          <span style={{ color: COLORS.gold }}>Maison Thaï · </span>
          Cuisine de rue thaïlandaise en barquettes individuelles
        </p>
      </div>
    </div>
  );
}

// Section narrative "Du marché à la barquette" : 4 photos avec captions
// Affichée uniquement sur la home boutique, juste après le hero principal.
function BarquetteSavoirFaireSection({ items, tagline }) {
  const { t } = useI18n();
  if (!items || items.length === 0) return null;
  return (
    <section
      className="relative w-full border-b py-12 sm:py-16 md:py-20"
      style={{ background: "#0D0D0D", borderColor: "rgba(242,236,228,0.06)" }}
      data-testid="barquette-savoir-faire"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Intro */}
        <div className="max-w-3xl mb-8 sm:mb-12">
          <p
            className="text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-3 sm:mb-4"
            style={{ color: COLORS.gold }}
          >
            {t("savoir_faire.kicker")}
          </p>
          <h2
            className="font-heading text-2xl sm:text-3xl md:text-4xl tracking-tight leading-[1.1] mb-4 sm:mb-5"
            style={{ color: COLORS.primary }}
          >
            {t("savoir_faire.title_1")} <em style={{ color: COLORS.chili, fontStyle: "italic", fontWeight: 400 }}>{t("savoir_faire.title_2")}</em>
          </h2>
          <p
            className="font-body text-sm sm:text-base leading-relaxed"
            style={{ color: COLORS.secondary }}
          >
            {tagline || t("savoir_faire.default_body")}
          </p>
        </div>

        {/* Strip 4 photos narratif horizontal */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
          {items.slice(0, 4).map((it, idx) => (
            <figure
              key={it.slug}
              className="group relative overflow-hidden flex flex-col"
              data-testid={`savoir-faire-step-${idx + 1}`}
            >
              <div
                className="aspect-[4/5] w-full overflow-hidden relative"
                style={{ background: "#111" }}
              >
                <ProtectedImg
                  src={`${API}/api/shop/boutique-section/${it.slug}/photo${it.uploaded_at ? `?v=${encodeURIComponent(it.uploaded_at)}` : ""}`}
                  alt={it.caption || `Étape ${idx + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.85) 100%)",
                  }}
                />
                <span
                  className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3 px-2 py-0.5 sm:py-1 text-[9px] sm:text-[10px] tracking-[0.25em] uppercase font-body"
                  style={{
                    background: "rgba(10,10,10,0.85)",
                    color: COLORS.gold,
                    border: `1px solid rgba(193,160,99,0.45)`,
                    backdropFilter: "blur(4px)",
                  }}
                >
                  {t("product_detail.step")} {idx + 1}
                </span>
              </div>
              <figcaption
                className="font-body text-[11px] sm:text-xs md:text-sm leading-snug px-1 pt-3 pb-1"
                style={{ color: COLORS.primary }}
              >
                {it.caption || ""}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function ShopFooter({ logo }) {
  const { t } = useI18n();
  const logoUrl = logo?.slug
    ? `${API}/api/shop/boutique-section/${logo.slug}/photo.jpg${logo.uploaded_at ? `?v=${encodeURIComponent(logo.uploaded_at)}` : ""}`
    : null;
  return (
    <footer
      className="w-full py-12 sm:py-16 md:py-20 px-6 md:px-12 border-t mt-16 sm:mt-24"
      style={{ background: "#050505", borderColor: "rgba(242,236,228,0.08)" }}
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 sm:gap-12">
        <div className="sm:col-span-2 md:col-span-1">
          {/* Logo optionnel (uploadable via /parametres/photos-boutique slug=logo-shop).
             Affiché uniquement si l'endpoint /boutique-section renvoie un `logo` non-nul. */}
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Maison Thaï"
              className="mb-3 max-h-16 sm:max-h-20 object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="footer-logo-image"
            />
          )}
          <h3
            className="text-3xl sm:text-4xl md:text-5xl mb-3 sm:mb-4"
            style={{ fontFamily: "'Sayonara', cursive", color: COLORS.primary, fontWeight: 700 }}
          >
            Maison Thaï <span style={{ color: COLORS.gold }}>Conthey</span>
          </h3>
          <p className="font-body text-sm leading-relaxed" style={{ color: COLORS.secondary }}>
            {t("footer.tagline")}
          </p>
        </div>
        <div className="space-y-2 font-body text-sm" style={{ color: COLORS.secondary }}>
          <h4 className="font-body uppercase tracking-[0.2em] text-xs mb-3" style={{ color: COLORS.muted }}>{t("footer.contact")}</h4>
          <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /> Rue de Parcouret 22<br />CH-1964 Conthey</div>
          <div className="flex items-center gap-2"><Mail className="w-4 h-4 flex-shrink-0" /> <a href="mailto:info@maison-thai.ch" className="hover:underline break-all">info@maison-thai.ch</a></div>
          <div className="flex items-center gap-2"><Phone className="w-4 h-4 flex-shrink-0" /> <a href="tel:+41273460775" className="hover:underline">+41 27 346 07 75</a></div>
          {/* Phase 102 — Icônes réseaux sociaux, affichées uniquement si URL renseignée */}
          <SocialIconsRow />
        </div>
        <div className="font-body text-sm" style={{ color: COLORS.secondary }}>
          <h4 className="uppercase tracking-[0.2em] text-xs mb-3" style={{ color: COLORS.muted }}>{t("footer.maison")}</h4>
          <nav className="flex flex-col gap-2">
            <Link to="/boutique/histoire" className="hover:underline transition-colors" data-testid="footer-story">{t("footer.history")}</Link>
            <Link to="/boutique/mentions" className="hover:underline transition-colors" data-testid="footer-mentions">{t("footer.legal")}</Link>
            <Link to="/boutique/cgv" className="hover:underline transition-colors" data-testid="footer-cgv">{t("footer.cgv")}</Link>
            <Link to="/boutique/confidentialite" className="hover:underline transition-colors" data-testid="footer-privacy">{t("footer.privacy")}</Link>
          </nav>
          <p className="text-[11px] mt-4 leading-relaxed" style={{ color: COLORS.muted }}>
            {t("footer.made_in")}
          </p>
        </div>
        <div className="font-body text-sm" style={{ color: COLORS.secondary }}>
          <h4 className="uppercase tracking-[0.2em] text-xs mb-3" style={{ color: COLORS.muted }}>{t("footer.pro")}</h4>
          <p className="text-[12px] leading-relaxed mb-3" style={{ color: COLORS.muted }}>
            {t("footer.pro_tagline")}
          </p>
          <Link
            to="/devenir-revendeur"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded border text-xs uppercase tracking-[0.18em] transition-all hover:bg-[rgba(242,236,228,0.08)]"
            style={{ borderColor: COLORS.gold, color: COLORS.gold }}
            data-testid="footer-become-reseller"
          >
            {t("footer.become_reseller")}
          </Link>
          <Link
            to="/hote-distributeur"
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 rounded border text-xs uppercase tracking-[0.18em] transition-all hover:bg-[rgba(242,236,228,0.08)]"
            style={{ borderColor: "rgba(242,236,228,0.35)", color: COLORS.secondary }}
            data-testid="footer-become-host"
            title="Accueillir un distributeur — Valais uniquement"
          >
            Devenir hôte distributeur
          </Link>
        </div>
      </div>
      <div className="max-w-7xl mx-auto mt-10 sm:mt-12 pt-6 border-t flex flex-col sm:flex-row sm:justify-between gap-2 text-[10px] uppercase tracking-[0.2em]" style={{ borderColor: "rgba(242,236,228,0.05)", color: COLORS.muted }}>
        <span>© {new Date().getFullYear()} Maison Thaï</span>
        <span
          className="normal-case tracking-normal"
          title="Marque enregistrée auprès de l'Institut Fédéral de la Propriété Intellectuelle (IPI · IGE), Berne"
          data-testid="footer-ipi-mention"
        >
          « Maison Thaï » <sup>®</sup> — marque déposée à l&apos;IPI
        </span>
        <span>maison-thai.ch</span>
      </div>
    </footer>
  );
}

// Phase 102 (12/07/2026) — Petite rangée d'icônes Facebook / Instagram dans le
// footer boutique. Rend rien si aucune URL renseignée. Ouvre en nouvel onglet.
function SocialIconsRow() {
  const { facebook_url, instagram_url } = React.useContext(BoutiqueSectionContext);
  const fb = (facebook_url || "").trim();
  const ig = (instagram_url || "").trim();
  if (!fb && !ig) return null;
  return (
    <div className="flex items-center gap-3 pt-2 mt-1" data-testid="footer-social-row">
      {fb && (
        <a
          href={fb}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center w-8 h-8 rounded-full border transition-all hover:scale-105"
          style={{ borderColor: "rgba(242,236,228,0.25)", color: COLORS.gold }}
          data-testid="footer-facebook-link"
          title="Suivez-nous sur Facebook"
          aria-label="Facebook"
        >
          <Facebook className="w-4 h-4" />
        </a>
      )}
      {ig && (
        <a
          href={ig}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center w-8 h-8 rounded-full border transition-all hover:scale-105"
          style={{ borderColor: "rgba(242,236,228,0.25)", color: COLORS.gold }}
          data-testid="footer-instagram-link"
          title="Suivez-nous sur Instagram"
          aria-label="Instagram"
        >
          <Instagram className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

// Contexte partagé pour la section boutique (top bar + savoir-faire + réseaux sociaux Phase 102)
// Hydraté une seule fois au montage du layout, accessible depuis ShopHome.
const BoutiqueSectionContext = React.createContext({ topbar: null, savoir_faire: [], logo: null, tagline: "", facebook_url: "", instagram_url: "" });

export function ShopLayout() {
  const [boutiqueSection, setBoutiqueSection] = useState({ topbar: null, savoir_faire: [], logo: null, tagline: "", facebook_url: "", instagram_url: "" });

  useEffect(() => {
    fetch(`${API}/api/shop/boutique-section`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setBoutiqueSection({
        topbar: d.topbar || null,
        savoir_faire: d.savoir_faire || [],
        logo: d.logo || null,
        tagline: d.tagline || "",
        facebook_url: d.facebook_url || "",
        instagram_url: d.instagram_url || "",
      }))
      .catch(() => {/* silencieux : le rendu fonctionne aussi sans cette section */});
  }, []);

  return (
    <div className="min-h-screen font-body" style={{ background: COLORS.bg, color: COLORS.primary }} data-testid="shop-layout">
      <ShopHeader />
      {/* Wrapper qui pousse les bannières sous le header fixed (sinon elles seraient masquées) */}
      <div className="pt-[56px] sm:pt-[72px] md:pt-[88px]">
        <DeliveryBanner />
        <BrandStripeBar topbar={boutiqueSection.topbar} />
      </div>
      <main>
        <BoutiqueSectionContext.Provider value={boutiqueSection}>
          <Outlet />
        </BoutiqueSectionContext.Provider>
      </main>
      <BoutiqueSectionContext.Provider value={boutiqueSection}>
        <ShopFooter logo={boutiqueSection.logo} />
      </BoutiqueSectionContext.Provider>
      <MobileCartBar />
    </div>
  );
}

// ============== Catalog page ==============
function CategoryChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="font-body text-xs sm:text-sm uppercase tracking-[0.15em] sm:tracking-[0.18em] py-2 transition-all border-b-2 whitespace-nowrap shrink-0"
      style={{
        color: active ? COLORS.primary : COLORS.muted,
        borderColor: active ? COLORS.chili : "transparent",
        fontWeight: active ? 500 : 400,
      }}
    >
      {children}
    </button>
  );
}

function ProductCard({ p, onClick, eager = false }) {
  const { t, lang } = useI18n();
  const name = localized(p, "name", lang);
  const shortDesc = localized(p, "short_description", lang);
  return (
    <article
      className="group cursor-pointer flex flex-col gap-4"
      onClick={onClick}
      data-testid={`product-card-${p.id}`}
    >
      <div className="aspect-[4/3] w-full overflow-hidden relative" style={{ background: "#111111" }}>
        <ProtectedImg
          src={resolvePhotoUrl(p.photo_url, 600)}
          srcSet={`${resolvePhotoUrl(p.photo_url, 400)} 400w, ${resolvePhotoUrl(p.photo_url, 600)} 600w, ${resolvePhotoUrl(p.photo_url, 800)} 800w`}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          alt={name}
          loading={eager ? "eager" : "lazy"}
          fetchPriority={eager ? "high" : "auto"}
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
          style={{ opacity: p.sold_out ? 0.45 : 0.92, filter: p.sold_out ? "grayscale(1)" : undefined }}
          onLoad={(e) => { if (!p.sold_out) e.target.style.opacity = 1; }}
        />
        {p.sold_out && (
          <span
            className="absolute inset-x-0 bottom-0 py-1.5 text-center text-[10px] sm:text-xs tracking-[0.3em] uppercase font-semibold"
            style={{ background: "rgba(10,10,10,0.8)", color: COLORS.primary }}
            data-testid={`sold-out-badge-${p.id}`}
          >
            {t("add_to_cart.sold_out")}
          </span>
        )}
        {/* Catégorie en haut-gauche */}
        <span
          className="absolute top-4 left-4 text-[10px] tracking-[0.25em] uppercase px-3 py-1 backdrop-blur-md border"
          style={{ background: "rgba(0,0,0,0.55)", color: COLORS.primary, borderColor: "rgba(242,236,228,0.15)" }}
        >
          {p.category}
        </span>
        {/* Niveau piquant en haut-droit */}
        {p.spice_level > 0 && (
          <div className="absolute top-4 right-4 px-2.5 py-1.5 backdrop-blur-md" style={{ background: "rgba(0,0,0,0.55)" }}>
            <SpiceIcons level={p.spice_level} size={12} />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1.5 sm:gap-2 pt-2 border-t" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
        <h3 className="font-heading text-base sm:text-2xl leading-tight transition-colors group-hover:text-[#E54D2E]" style={{ color: COLORS.primary }}>
          {name.replace(", riz thaï", "").replace(", riz parfumé thaï", "")}
        </h3>
        {shortDesc && (
          <p className="font-body text-[11px] sm:text-sm leading-relaxed line-clamp-2" style={{ color: COLORS.secondary }}>
            {shortDesc}
          </p>
        )}
        <div className="flex justify-between items-baseline mt-1 gap-2">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="font-body text-sm sm:text-lg font-medium" style={{ color: COLORS.gold }}>
              {p.price_online != null ? `${Number(p.price_online).toFixed(2)} CHF` : "—"}
            </span>
            {p.is_reseller_price && p.price_online_original != null && (
              <span className="font-body text-[10px] sm:text-xs line-through" style={{ color: COLORS.muted }} title="Prix B2C standard">
                {Number(p.price_online_original).toFixed(2)}
              </span>
            )}
          </div>
          {p.net_weight && (
            <span className="font-body text-[10px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.15em] whitespace-nowrap" style={{ color: COLORS.muted }}>
              {p.net_weight} {p.net_weight_unit || "g"}
            </span>
          )}
        </div>
        {/* Micro-badge packaging : rassure le client sur le conditionnement réel */}
        <div
          className="hidden sm:flex items-center gap-1.5 mt-1 text-[10px] uppercase tracking-[0.18em]"
          style={{ color: COLORS.muted }}
          data-testid={`packaging-badge-${p.id}`}
        >
          <Package className="w-3 h-3" strokeWidth={1.5} />
          <span>
            {t("product_card.served_in_tray")}
            {p.net_weight ? ` · ${p.net_weight}${p.net_weight_unit || "g"}` : ""} · MAP
          </span>
        </div>
        <CatalogQtyControl product={p} />
      </div>
    </article>
  );
}

export function ShopCatalog() {
  const { customer, reseller } = useShopAuth() || {};
  const { t } = useI18n();
  const [products, setProducts] = useState(catalogCache || []);
  const [loading, setLoading] = useState(!catalogCache);
  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategoryState] = useState(searchParams.get("cat") || "__all__");
  const [spiceLevel, setSpiceLevel] = useState(null); // null = pas de filtre, sinon valeur exacte
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  const gridRef = useRef(null);
  const navigate = useNavigate();
  const boutiqueSection = React.useContext(BoutiqueSectionContext);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Retour depuis une fiche : on revient exactement là où l'on était dans la liste
  useEffect(() => {
    const saved = sessionStorage.getItem(CATALOG_SCROLL_KEY);
    if (loading || !products.length || saved == null) return;
    sessionStorage.removeItem(CATALOG_SCROLL_KEY);
    requestAnimationFrame(() => window.scrollTo(0, Number(saved)));
  }, [loading, products.length]);

  const openProduct = (id) => {
    sessionStorage.setItem(CATALOG_SCROLL_KEY, String(window.scrollY));
    navigate(`/boutique/produit/${id}`, { state: { fromCatalog: true } });
  };

  // Synchronise la catégorie avec l'URL (utile pour le retour depuis le breadcrumb)
  const setCategory = (c) => {
    setCategoryState(c);
    const next = new URLSearchParams(searchParams);
    if (c === "__all__") next.delete("cat");
    else next.set("cat", c);
    setSearchParams(next, { replace: true });
    requestAnimationFrame(() => {
      const top = gridRef.current?.getBoundingClientRect().top;
      if (top != null && top < 0) window.scrollTo({ top: window.scrollY + top - 120, behavior: "smooth" });
    });
  };

  // Si l'URL change (navigation arrière), on resync l'état
  useEffect(() => {
    const cat = searchParams.get("cat") || "__all__";
    setCategoryState(cat);
  }, [searchParams]);

  // Recharge le catalogue quand l'auth du client change (pour que les prix B2B
  // soient appliqués automatiquement après login/logout, sans rafraîchir la page)
  useEffect(() => {
    if (customer === null) return; // session encore en cours de vérification
    if (!catalogCache) setLoading(true);
    fetch(`${API}/api/shop/products`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => { catalogCache = data; setProducts(data); })
      .catch(() => setProducts(catalogCache || []))
      .finally(() => setLoading(false));
  }, [customer]);

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => p.category && set.add(p.category));
    return Array.from(set).sort();
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (category !== "__all__" && p.category !== category) return false;
      if (spiceLevel != null && (p.spice_level || 0) !== spiceLevel) return false;
      return true;
    });
  }, [products, category, spiceLevel]);

  return (
    <div data-testid="shop-catalog">
      <LastOrderBanner />
      {/* Hero */}
      <section
        className="relative w-full overflow-hidden border-b"
        style={{ background: COLORS.bg, borderColor: "rgba(242,236,228,0.08)" }}
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "saturate(0.9)",
          }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.95) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-6 sm:py-20 md:py-32">
          <p className="text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase mb-2 sm:mb-6" style={{ color: COLORS.gold }}>
            {t("hero.kicker")}
          </p>
          <h1 className="font-heading text-2xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] max-w-4xl" style={{ color: COLORS.primary }}>
            {t("hero.title_1")}<br />
            <em style={{ color: COLORS.chili, fontStyle: "italic", fontWeight: 400 }}>{t("hero.title_2")}</em>
          </h1>
          <p className="hidden sm:block font-body text-sm sm:text-base md:text-lg mt-5 sm:mt-6 max-w-xl leading-relaxed" style={{ color: COLORS.secondary }}>
            {t("hero.subtitle")}
          </p>
        </div>
      </section>

      {/* Sur mobile, ces blocs passent sous la liste des plats */}
      {!isMobile && <CatalogStorySections boutiqueSection={boutiqueSection} />}

      {/* Phase 31 : Bandeau revendeur B2B */}
      {reseller && (
        <div
          className="border-y"
          style={{
            background: "linear-gradient(90deg, rgba(136,160,102,0.18) 0%, rgba(136,160,102,0.06) 100%)",
            borderColor: "rgba(136,160,102,0.4)",
          }}
          data-testid="reseller-banner"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-3 sm:py-4 flex items-center gap-3 flex-wrap">
            <span
              className="inline-flex items-center px-2 py-0.5 rounded text-[10px] tracking-[0.2em] uppercase font-semibold"
              style={{ background: "rgba(136,160,102,0.35)", color: COLORS.basil }}
            >
              {t("reseller_banner.badge")}
            </span>
            <p className="text-sm sm:text-base font-body" style={{ color: COLORS.primary }}>
              {t("reseller_banner.welcome")} <strong>{reseller.name}</strong> {t("reseller_banner.intro")}
              {reseller.discount_percent != null && (
                <>
                  {" "}{t("reseller_banner.with_discount")}{" "}
                  <strong style={{ color: COLORS.basil }}>−{reseller.discount_percent}%</strong>
                </>
              )}
              {" "}{t("reseller_banner.applied")}
            </p>
          </div>
        </div>
      )}

      {/* Filters sticky */}
      <div
        className="sticky top-[56px] sm:top-[72px] z-30 backdrop-blur-md border-b"
        style={{ background: "rgba(10,10,10,0.92)", borderColor: "rgba(242,236,228,0.05)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-2 sm:py-4 flex flex-nowrap overflow-x-auto sm:flex-wrap gap-x-4 sm:gap-x-5 md:gap-x-7 gap-y-2 items-center [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="catalog-filters-row">
          <CategoryChip active={category === "__all__"} onClick={() => setCategory("__all__")} testid="filter-all">
            {t("filters.all")}
          </CategoryChip>
          {categories.map((c) => (
            <CategoryChip key={c} active={category === c} onClick={() => setCategory(c)} testid={`filter-cat-${c.toLowerCase()}`}>
              {c}
            </CategoryChip>
          ))}
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2 pl-3 sm:pl-6 border-l shrink-0" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
            <Flame className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color: spiceLevel != null ? COLORS.chili : COLORS.muted }} />
            <select
              value={spiceLevel ?? "any"}
              onChange={(e) => setSpiceLevel(e.target.value === "any" ? null : Number(e.target.value))}
              data-testid="filter-spice-select"
              className="bg-transparent border-0 text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.18em] py-2 cursor-pointer focus:outline-none appearance-none pr-5"
              style={{
                color: spiceLevel != null ? COLORS.chili : COLORS.muted,
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='none' stroke='%23737373' stroke-width='1.5' d='M1 1l4 4 4-4'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 0 center",
              }}
            >
              <option value="any" style={{ background: COLORS.surface }}>{t("filters.spice_any")}</option>
              <option value="0" style={{ background: COLORS.surface }}>{t("filters.spice_mild")}</option>
              <option value="1" style={{ background: COLORS.surface }}>{t("filters.spice_l1")}</option>
              <option value="2" style={{ background: COLORS.surface }}>{t("filters.spice_l2")}</option>
              <option value="3" style={{ background: COLORS.surface }}>{t("filters.spice_l3")}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Grid */}
      <section ref={gridRef} className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-5 sm:py-16 md:py-24" data-testid="catalog-grid-section">
        <ShopCmsBanner slug="delais_livraison" className="mb-5 sm:mb-12 md:mb-14 max-w-3xl mx-auto" />
        <ShopCmsBanner slug="promo_moment" className="mb-5 sm:mb-12 md:mb-14 max-w-3xl mx-auto" />
        {loading ? (
          <div className="text-center py-20 sm:py-32 font-body text-xs sm:text-sm tracking-widest uppercase" style={{ color: COLORS.muted }}>
            {t("catalog.loading")}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 sm:py-32">
            <p className="font-heading text-2xl sm:text-3xl mb-4" style={{ color: COLORS.primary }}>{t("catalog.empty_title")}</p>
            <p className="font-body text-sm" style={{ color: COLORS.secondary }}>
              {t("catalog.empty_body")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 md:gap-14">
            {filtered.map((p, idx) => (
              <ProductCard
                key={p.id}
                p={p}
                eager={idx < 3}
                onClick={() => openProduct(p.id)}
              />
            ))}
          </div>
        )}
      </section>
      {isMobile && <CatalogStorySections boutiqueSection={boutiqueSection} />}
    </div>
  );
}

function CatalogStorySections({ boutiqueSection }) {
  return (
    <>
      {/* Mini-bloc envoi national isotherme */}
      <IsothermShippingNote />
      {/* Section narrative "Du marché à la barquette" */}
      <BarquetteSavoirFaireSection items={boutiqueSection.savoir_faire} tagline={boutiqueSection.tagline} />
      {/* Phase 100 — Bannière « Devenir hôte distributeur », FR uniquement, ciblage Valais */}
      <HostRecruitmentBanner />
    </>
  );
}

// ============== Detail page ==============
const NUT_LABELS = {
  energy_kj: { label: "Énergie", unit: "kJ" },
  energy_kcal: { label: "Énergie", unit: "kcal" },
  fat: { label: "Matières grasses", unit: "g" },
  saturated_fat: { label: "dont acides gras saturés", unit: "g", indent: true },
  carbs: { label: "Glucides", unit: "g" },
  sugars: { label: "dont sucres", unit: "g", indent: true },
  proteins: { label: "Protéines", unit: "g" },
  salt: { label: "Sel", unit: "g" },
};

export function ShopProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { customer } = useShopAuth() || {};
  const { t, lang } = useI18n();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);
  const backToCatalog = () => (location.state?.fromCatalog ? navigate(-1) : navigate("/boutique"));

  useEffect(() => {
    if (customer === null) return; // session encore en cours
    setLoading(true);
    fetch(`${API}/api/shop/products/${id}`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error("404"); return r.json(); })
      .then(setP)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id, customer]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-xs uppercase tracking-widest" style={{ color: COLORS.muted }}>{t("product_detail.loading")}</div>;
  if (notFound || !p) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
      <p className="font-heading text-3xl" style={{ color: COLORS.primary }}>{t("product_detail.not_found")}</p>
      <Link to="/boutique" className="font-body text-sm uppercase tracking-widest" style={{ color: COLORS.chili }}>← {t("product_detail.back_to_catalog")}</Link>
    </div>
  );

  const nutritionEntries = p.nutrition
    ? Object.entries(NUT_LABELS).filter(([k]) => p.nutrition[k] != null)
    : [];

  // Champs localisés (DE/IT/FR) — Phase 53
  const pName = localized(p, "name", lang);
  const pShortDesc = localized(p, "short_description", lang);
  const pDescription = localized(p, "description", lang);
  const pIngredients = localized(p, "ingredients", lang);
  const pStorage = localized(p, "storage_instructions", lang);
  const pReheating = localized(p, "reheating_instructions", lang);

  return (
    <div className="lg:grid lg:grid-cols-12 min-h-screen" data-testid="shop-product-detail">
      {/* Image massive + miniature packaging */}
      <div className="lg:col-span-7 lg:sticky lg:top-24 lg:h-[calc(100vh-6rem)] flex flex-col items-center justify-center gap-4 sm:gap-6 p-4 sm:p-6 md:p-12" style={{ background: COLORS.bg }}>
        <ProtectedImg
          src={resolvePhotoUrl(p.photo_url, 1200)}
          srcSet={`${resolvePhotoUrl(p.photo_url, 600)} 600w, ${resolvePhotoUrl(p.photo_url, 800)} 800w, ${resolvePhotoUrl(p.photo_url, 1200)} 1200w`}
          sizes="(max-width: 1024px) 100vw, 60vw"
          alt={p.name}
          decoding="async"
          className="max-h-[45vh] sm:max-h-[55vh] lg:max-h-[70%] max-w-full object-contain"
          style={p.sold_out ? { filter: "grayscale(1)", opacity: 0.5 } : undefined}
          data-testid="product-image"
        />
        {p.photo_packaging_url && (
          <figure
            className="flex items-center gap-4 px-5 py-3 border max-w-md"
            style={{ borderColor: "rgba(242,236,228,0.12)", background: "rgba(20,20,20,0.6)" }}
            data-testid="product-packaging-thumb"
          >
            <ProtectedImg
              src={resolvePhotoUrl(p.photo_packaging_url, 200)}
              alt={`Barquette ${p.name}`}
              loading="lazy"
              decoding="async"
              className="w-20 h-20 object-cover"
              style={{ borderRadius: 2 }}
            />
            <figcaption className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.25em]" style={{ color: COLORS.gold }}>
                {t("product_detail.packaging_label")}
              </span>
              <span className="font-body text-sm leading-snug" style={{ color: COLORS.primary }}>
                {t("product_detail.packaging_sealed")}
              </span>
              <span className="text-[11px]" style={{ color: COLORS.muted }}>
                {p.net_weight ? `${p.net_weight} ${p.net_weight_unit || "g"} · ` : ""}{t("product_detail.packaging_map")}
              </span>
            </figcaption>
          </figure>
        )}
      </div>

      {/* Détails */}
      <div className="lg:col-span-5 py-10 sm:py-14 lg:py-16 px-5 sm:px-8 md:px-12 lg:px-16 flex flex-col gap-8 sm:gap-10 lg:border-l" style={{ background: COLORS.surface, borderColor: "rgba(242,236,228,0.08)" }}>
        {/* Fil d'Ariane élégant */}
        <nav
          className="flex items-center flex-wrap gap-x-2 gap-y-1 text-[11px] uppercase tracking-[0.2em] font-body"
          aria-label="Fil d'Ariane"
          data-testid="breadcrumb"
        >
          <Link
            to="/boutique"
            className="transition-colors duration-300 hover:text-[#F2ECE4]"
            style={{ color: COLORS.gold }}
            data-testid="breadcrumb-catalog"
          >
            {t("product_detail.breadcrumb_catalog")}
          </Link>
          <span aria-hidden="true" style={{ color: "rgba(242,236,228,0.18)" }}>—</span>
          {p.category && (
            <>
              <Link
                to={`/boutique?cat=${encodeURIComponent(p.category)}`}
                className="transition-colors duration-300 hover:text-[#F2ECE4]"
                style={{ color: COLORS.secondary }}
                data-testid="breadcrumb-category"
              >
                {p.category}
              </Link>
              <span aria-hidden="true" style={{ color: "rgba(242,236,228,0.18)" }}>—</span>
            </>
          )}
          <span
            className="truncate max-w-[200px] normal-case tracking-[0.08em]"
            style={{ color: COLORS.muted, fontStyle: "italic" }}
            data-testid="breadcrumb-current"
          >
            {pName.replace(", riz thaï", "").replace(", riz parfumé thaï", "")}
          </span>
        </nav>

        <header className="space-y-3 sm:space-y-4">
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase" style={{ color: COLORS.gold }}>{p.category}</p>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight" style={{ color: COLORS.primary }}>
            {pName}
          </h1>
          <div className="flex items-center gap-4 sm:gap-6 pt-2 flex-wrap">
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="font-body text-xl sm:text-2xl font-medium" style={{ color: COLORS.gold }}>
                {p.price_online != null ? `${Number(p.price_online).toFixed(2)} CHF` : t("product_detail.price_on_request")}
              </span>
              {p.is_reseller_price && p.price_online_original != null && (
                <>
                  <span className="font-body text-sm line-through" style={{ color: COLORS.muted }} title="Prix conseillé public">
                    {Number(p.price_online_original).toFixed(2)} CHF
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] tracking-[0.15em] uppercase font-semibold" style={{ background: "rgba(136,160,102,0.18)", color: COLORS.basil }}>
                    {p.reseller_price_source === "override"
                      ? t("product_detail.reseller_negotiated")
                      : p.reseller_price_source === "discount" && p.reseller_discount_pct != null
                        ? `${t("product_detail.reseller_discount")} −${p.reseller_discount_pct}%`
                        : t("product_detail.reseller_tier")}
                  </span>
                </>
              )}
            </div>
            {p.net_weight && (
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.15em]" style={{ color: COLORS.muted }}>
                {p.net_weight} {p.net_weight_unit || "g"}
              </span>
            )}
            {p.spice_level > 0 && <SpiceIcons level={p.spice_level} size={16} />}
          </div>
        </header>

        {pShortDesc && (
          <p className="font-heading italic text-base sm:text-lg leading-relaxed" style={{ color: COLORS.primary }}>
            « {pShortDesc} »
          </p>
        )}

        {pDescription && pDescription !== pShortDesc && (
          <p className="font-body text-sm sm:text-base leading-relaxed" style={{ color: COLORS.secondary }}>
            {pDescription}
          </p>
        )}

        {/* CTA */}
        <AddToCartButton product={p} onAdded={backToCatalog} />
        <p className="text-[11px] text-center -mt-6 uppercase tracking-[0.2em]" style={{ color: COLORS.muted }}>
          {t("product_detail.cta_hint")}
        </p>

        {/* Allergens (mis en valeur : encadré chili discret + chips) */}
        {p.allergens?.length > 0 && (
          <div className="border-l-2 p-5" style={{ borderColor: COLORS.chili, background: "rgba(229,77,46,0.05)" }} data-testid="allergens-section">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-4 h-4" style={{ color: COLORS.chili }} />
              <h4 className="font-body text-xs uppercase tracking-[0.25em] font-semibold" style={{ color: COLORS.chili }}>
                {t("product_detail.allergens_title")} · {p.allergens.length}
              </h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {p.allergens.map((a) => (
                <span
                  key={a}
                  className="px-3 py-1 text-xs uppercase tracking-wider border rounded-full"
                  style={{
                    color: COLORS.primary,
                    borderColor: "rgba(229,77,46,0.35)",
                    background: "rgba(229,77,46,0.08)",
                  }}
                  data-testid={`allergen-${a.toLowerCase()}`}
                >
                  {a}
                </span>
              ))}
            </div>
            <p className="text-[10px] mt-3 italic" style={{ color: COLORS.muted }}>
              {t("product_detail.allergens_note")}
            </p>
          </div>
        )}

        {/* Ingredients */}
        {pIngredients && (
          <div data-testid="ingredients-section">
            <h4 className="font-body text-xs uppercase tracking-[0.25em] mb-3" style={{ color: COLORS.gold }}>
              {t("product_detail.ingredients")}
            </h4>
            <p className="font-body text-sm leading-relaxed" style={{ color: COLORS.secondary }}>
              {pIngredients}
            </p>
          </div>
        )}

        {/* Nutrition */}
        {nutritionEntries.length > 0 && (
          <div>
            <h4 className="font-body text-xs uppercase tracking-[0.25em] mb-3" style={{ color: COLORS.muted }}>{t("product_detail.nutrition_title")}</h4>
            <table className="w-full text-sm">
              <tbody>
                {nutritionEntries.map(([k, meta]) => (
                  <tr key={k} className="border-b" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
                    <td className={`py-2.5 font-body ${meta.indent ? "pl-4" : ""}`} style={{ color: meta.indent ? COLORS.muted : COLORS.secondary }}>
                      {meta.label}
                    </td>
                    <td className="py-2.5 text-right font-body font-medium tabular-nums" style={{ color: COLORS.primary }}>
                      {p.nutrition[k]} <span className="text-xs ml-1" style={{ color: COLORS.muted }}>{meta.unit}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Conservation & réchauffage */}
        {(pStorage || pReheating) && (
          <div className="space-y-4">
            {pStorage && (
              <div>
                <h4 className="font-body text-xs uppercase tracking-[0.25em] mb-2" style={{ color: COLORS.muted }}>{t("product_detail.storage")}</h4>
                <p className="font-body text-sm leading-relaxed" style={{ color: COLORS.primary }}>{pStorage}</p>
              </div>
            )}
            {pReheating && (
              <div>
                <h4 className="font-body text-xs uppercase tracking-[0.25em] mb-2" style={{ color: COLORS.muted }}>{t("product_detail.reheating")}</h4>
                <p className="font-body text-sm leading-relaxed" style={{ color: COLORS.primary }}>{pReheating}</p>
              </div>
            )}
          </div>
        )}

        <div className="text-[10px] uppercase tracking-[0.25em] pt-6 border-t" style={{ borderColor: "rgba(242,236,228,0.08)", color: COLORS.muted }}>
          {t("product_detail.ref")} {p.sku} · {t("product_detail.ref_footer")}
        </div>
      </div>
    </div>
  );
}
